/*
 * Copyright (c) Nexign, JSC, 2019-2020.
 */
/**
 *
 * @author Yakov Gushchin
 * @author Evgenij Tyan
 * @author Aleksandr Perkhunov
 * @version 1.2.0
 * @since 1.0.0
 */

import { FocusEvent } from 'react';

import { SagaIterator } from 'redux-saga';
import { put, select } from 'redux-saga/effects';

import { IOptionItemProps } from '../../../../../components/common/Select/Select';
import { makeSelectTranslation } from '../../../../../modules/internationalization/intSelectors';
import { IExtFilterTranslation } from '../../../../../modules/internationalization/types/pages/subscriberMobile';
import {
    setExtFilterIdSuccessful,
    setExtFilterItemsFailed,
    setExtFilterItemsSuccessful,
} from '../actions/extFilterConstructorActions';
import { ExtFilterConstructUtils } from '../components/ExtFilterConstructorBlock/ExtFilterConstructorBlockUitls';
import { makeSelectExtFilterItems, makeSelectExtFilterList, makeSelectExtFilterMeta } from '../extFilterSelectors';
import {
    ExtFilterAttrEnum,
    ExtFilterOperationsEnum,
    ExtFilterOperatorsEnum,
    IExtFilterItem,
    IExtFilterItemValue,
    IExtFilterMetaItem,
    ISubscriberMobileExtFilter,
} from '../types/extFilterTypes';

export function* updateExtFilterIdSaga(action: { payload: string; type: string }): SagaIterator {
    const extFilterList = yield select(makeSelectExtFilterList());
    const filter = yield extFilterList.find(
        (item: ISubscriberMobileExtFilter) => item.id === action.payload,
    );
    const newItems = filter ? filter.items : [{ operation: null }];
    yield put(setExtFilterIdSuccessful(action.payload));
    yield put(setExtFilterItemsSuccessful(newItems));
}

export function* updateExtFilterItemsSaga(action: { payload: IExtFilterItemValue; type: string }): SagaIterator {
    try {
        const extFilterMeta = yield select(makeSelectExtFilterMeta());
        const translation = yield select(makeSelectTranslation());
        const extFilterItems = yield select(makeSelectExtFilterItems());

        const newItems = getNewItems(
            action.payload,
            extFilterItems,
            extFilterMeta,
            translation.pageSubscriberMobile.extFilter,
        );
        yield put(setExtFilterItemsSuccessful(newItems));
    } catch (error: any) {
        yield put(setExtFilterItemsFailed(error));
    }
}

export function getNewItems(
    options: IExtFilterItemValue,
    prevItems: IExtFilterItem[],
    extFilterMeta: IExtFilterMetaItem[],
    translation: IExtFilterTranslation,
): IExtFilterItem[] {
    const index = options.index;
    const items = [...prevItems];
    const eventValue = options.value;
    const utils = new ExtFilterConstructUtils({ extFilterMeta, translation });
    switch (options.type) {
        case 'attr':
            const attr = (options.value as IOptionItemProps).value as ExtFilterAttrEnum;
            items[index] = utils.getNewItem(attr, items[index], index);
            return items;
        case 'operators':
            const operator = (eventValue as IOptionItemProps).value as ExtFilterOperatorsEnum;
            items[index] = { ...items[index], operator };
            return items;
        case 'operation':
            const operation = (eventValue as IOptionItemProps).value as ExtFilterOperationsEnum;
            items[index] = { ...items[index], operation };
            return items;
        case 'valueText':
            const target = (eventValue as FocusEvent<HTMLInputElement>).target;
            const textValue = target ? target.value : '';
            items[index] = { ...items[index], value: textValue, valueName: textValue };
            return items;
        case 'valueSelect':
            const { value, label } = (eventValue as IOptionItemProps);
            items[index] = { ...items[index], value: value.toString(), valueName: label };
            return items;
        case 'add':
            return [...items, { operation: ExtFilterOperationsEnum.AND }];
        case 'delete':
            return items.filter((item: IExtFilterItem, i: number) => i !== index);
        default:
            return items;
    }
}
